import React, { useCallback } from 'react'

import { useTranslation } from 'react-i18next'

import CloseIcon from '@mui/icons-material/Close'
import { AutocompleteRenderGetTagProps, AutocompleteRenderInputParams, Autocomplete } from '@mui/material'

import { Disease } from '../../../ana-toolbox'
import { TextField } from '../../utils/shared/TextField'
import { Tag } from '../tag/tag'
import { Translate } from '../translate/translate'

import * as S from './disease-field.styles'

type Props = {
  index: number
  disease: Disease
  hideMedications?: boolean
  customInputPlaceholder?: string
  checked?: boolean
  isCustom?: boolean
  autoFocus?: boolean
  isLast?: boolean
  onCheck?: (event: React.ChangeEvent<{ name: string }>, checked: boolean) => void
  onChange?: (disease: Disease) => void
  onDelete?: (disease: Disease) => void
}

const renderTags = (values: string[], getTagProps: AutocompleteRenderGetTagProps) => {
  return values.map((value, index) => <Tag label={value} {...getTagProps({ index })} key={`tag-${value}`} />)
}

const DiseaseField: React.FC<Props> = ({
  index,
  disease,
  checked,
  customInputPlaceholder,
  autoFocus,
  isCustom,
  isLast,
  hideMedications,
  onCheck,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation()

  const handleMedicationChange = useCallback(
    (_: unknown, medications: string[]) => {
      if (onChange) onChange({ ...disease, medications })
    },
    [disease, onChange]
  )

  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(disease)
  }, [onDelete, disease])

  const handleAutocompleteInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const enterKey = 13

      if (event.keyCode === enterKey && event.currentTarget.value && onChange) {
        // Prevent the form submit
        event.preventDefault()
        const medications = disease.medications || []

        onChange({ ...disease, medications: [...medications, event.currentTarget.value] })
      }
    },
    [disease, onChange]
  )

  const handleAutocompleteInputBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (event.currentTarget.value.length > 0 && onChange) {
        const medications = disease.medications || []

        onChange({ ...disease, medications: [...medications, event.currentTarget.value] })
      }
    },
    [disease, onChange]
  )

  const handleCustomDiseaseChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange({ ...disease, name: event.currentTarget.value })
      }
    },
    [onChange, disease]
  )

  const handleCustomDiseaseKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()

        document.querySelector<HTMLInputElement>(`[data-input-id=${disease.type}-${index + 1}]`)?.focus()
      }
    },
    [disease.type, index]
  )

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      return (
        <TextField
          {...params}
          variant="outlined"
          placeholder={t('subcategory.medication', { postProcess: [] }) || ''}
          label={<Translate>field.medication</Translate>}
          helperText={<Translate>field.which_medication</Translate>}
          InputProps={{
            ...params.InputProps,
            className: undefined,
            endAdornment: undefined,
            onFocus: undefined,
            onKeyDown: handleAutocompleteInputKeyDown,
            onBlur: handleAutocompleteInputBlur,
            autoFocus: !isCustom,
          }}
        />
      )
    },
    [handleAutocompleteInputBlur, handleAutocompleteInputKeyDown, t, isCustom]
  )

  return (
    <>
      <S.Container>
        <S.FormControlLabel
          label={
            isCustom ? (
              <S.CustomInput
                onChange={handleCustomDiseaseChange}
                onKeyPress={handleCustomDiseaseKeyPress}
                placeholder={t(customInputPlaceholder || 'option.other', { defaultValue: t('option.other') }) || ''}
                defaultValue={disease.name}
                autoFocus={autoFocus}
                data-input-id={`${disease.type}-${index}`}
              />
            ) : (
              <Translate>{t(`option.${disease.name}`, { defaultValue: disease.name })}</Translate>
            )
          }
          labelPlacement="end"
          control={
            isCustom && disease.name === '' ? (
              <span />
            ) : (
              <S.Checkbox checked={checked} onChange={onCheck} name={disease.name} disableRipple disableFocusRipple />
            )
          }
        />

        {isCustom && !isLast && (
          <S.DeleteButton onClick={handleDelete}>
            <CloseIcon />
          </S.DeleteButton>
        )}
      </S.Container>

      {checked && !hideMedications && (
        <S.Container inputWrapper>
          <S.AutocompleteWrapper>
            <Autocomplete
              slotProps={{
                popper: {
                  sx: { display: 'none' },
                },
              }}
              options={[] as string[]}
              value={disease.medications || []}
              onChange={handleMedicationChange}
              renderInput={renderInput}
              renderTags={renderTags}
              filterSelectedOptions
              multiple
            />
          </S.AutocompleteWrapper>
        </S.Container>
      )}
    </>
  )
}

export default DiseaseField
